// See NotificationBar.spec.md for NB-FR-001, NB-FR-002, NB-FR-004 and NB-FR-006 (the bar's styles)

import { makeStyles } from '@material-ui/styles'
import type Theme from 'core/themes/model'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import { CONTROLS_HEIGHT } from './NotificationBarDrawer'

const useNotificationBarStyles = makeStyles<Theme>((theme) => ({
  // UX design: a white panel (the drawer's table background), not the frame's grey
  // NB-FR-001: Pinned to the bottom of its frame row; an open drawer grows it upward over the page
  bar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    // Lifts the bar off the page behind it
    boxShadow: `0 -2px 8px ${hexToRgbaCss(theme.palette.grey[900], 0.08)}`,
    backgroundColor: theme.components.table.background,
    // The Events label reaches below the controls (onto the drawer's border); on a collapsed bar
    // that would hang past the bottom of the window
    overflow: 'hidden',
  },
  handle: {
    height: 3,
    marginBottom: -3,
    position: 'relative',
    zIndex: 2,
    cursor: 'ns-resize',
    touchAction: 'none',
    '&:hover, &:active': {
      outline: 'none',
    },
    // NB-FR-004: Keyboard focus on the handle stays visible (WCAG 2.4.7)
    '&:focus-visible': {
      outline: 'none',
      backgroundColor: hexToRgbaCss(theme.components.tab.activeBackground, 0.6),
    },
  },
  controlsCollapsed: {
    cursor: 'pointer',
  },
  toggle: {
    '& i': {
      fontSize: 14,
    },
  },
  controls: {
    display: 'flex',
    // Groups take the row's full height, so the label can sit on its bottom edge
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1),
    height: CONTROLS_HEIGHT,
    boxSizing: 'border-box',
  },
  group: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  // NB-FR-002: The full height of the controls row: the label is centred with the other controls
  // and the underline sits at the bottom, on the drawer's top border. Same typography and
  // underline as the app's tabs (core/elements/tabs/TabPreview).
  tab: {
    display: 'grid',
    alignSelf: 'stretch',
    marginLeft: theme.spacing(3),
    // The label is centred in the space above the underline's row, which is kept when the
    // underline is hidden, so the label doesn't move
    gridTemplateRows: '1fr 4px',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    // Reaches past the controls' padding and onto the drawer's 1 px top border
    marginBottom: theme.spacing(-1) - 1,
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0, 3),
  },
  tabText: {
    color: theme.components.tab.activeText,
  },
  // NB-FR-002: Sits on the drawer's top border (drawn over it), like the app's tab strips
  tabUnderline: {
    position: 'relative',
    zIndex: 1,
    // Same thickness as the app's tab strips (core/elements/tabs/TabPreview)
    height: 4,
    borderRadius: 1.5,
    background: theme.components.tab.activeBackground,
  },
  // NB-FR-006 (Figma): the count on a grey chip, then a blue dot
  count: {
    fontSize: 11,
    lineHeight: '16px',
    minWidth: 16,
    padding: theme.spacing(0, 0.5),
    borderRadius: 4,
    textAlign: 'center',
    color: theme.components.tab.activeText,
    backgroundColor: theme.components.tab.border,
  },
  dot: {
    width: 7,
    height: 7,
    marginLeft: theme.spacing(-0.75),
    borderRadius: '50%',
    alignSelf: 'flex-start',
    backgroundColor: theme.components.tab.activeBackground,
  },
  betaTag: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0, 0.75),
    border: `1px solid ${theme.components.tab.activeBackground}`,
    borderRadius: 4,
    fontSize: 11,
    lineHeight: '16px',
    fontWeight: 600,
    color: theme.components.tab.activeBackground,
  },
  // Figma: the search box and timeframe are outlined and as tall (36 px) as the Notification
  // Center button; their compact variants have no border and differ in height
  search: {
    width: 275,
    '& .inputFrame': {
      borderWidth: 1,
      height: 36,
      boxSizing: 'border-box',
    },
    '& .input': {
      minHeight: 0,
      height: '100%',
    },
  },
  timeframe: {
    '& .inputFrame': {
      borderWidth: 1,
      minHeight: 36,
    },
  },
  divider: {
    width: 1,
    height: 24,
    backgroundColor: theme.components.card.border,
  },
}))

export default useNotificationBarStyles
